import { ACTIVE_OR_BANNED } from "@/api/custom_types";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { getRelationQueryTranslation } from "@/utils/translations";
import {
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";
import { z } from "zod";

const filterSchema = z.object({
	buyer_name: z.string().min(6).max(32).nullable(),
	seller_name: z.string().min(6).max(32).nullable(),
	status: z.array(z.enum(ACTIVE_OR_BANNED)).default([]),
});
export type FilterFormData = z.infer<typeof filterSchema>;

interface RelationsTableFilters {
	buyer_name: string;
	seller_name: string;
	status: string[];
}

function toTableFilters(values?: FilterFormData): RelationsTableFilters {
	return {
		buyer_name: values?.buyer_name ?? "",
		seller_name: values?.seller_name ?? "",
		status: values?.status ? [...values.status] : [],
	};
}

interface FilterFormProps {
	onSearch: (values: FilterFormData) => void;
	defaultValues?: FilterFormData;
	isSeller: boolean;
}

export const RelationsFilterForm: React.FC<FilterFormProps> = ({
	onSearch,
	defaultValues,
	isSeller = false,
}) => {
	const { t } = useTranslation();

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<RelationsTableFilters>({
			initialFilters: toTableFilters(defaultValues),
			onApply: (next) =>
				onSearch({
					buyer_name: next.buyer_name || null,
					seller_name: next.seller_name || null,
					status: next.status as FilterFormData["status"],
				}),
			textFilterKeys: ["buyer_name", "seller_name"],
		});

	const statusOptions = useMemo(() => {
		return ACTIVE_OR_BANNED.map((status) => {
			const { text } = getRelationQueryTranslation(status, t);
			return { value: status, label: text as string };
		});
	}, [t]);

	const config = useMemo<FilterConfig<RelationsTableFilters>>(() => {
		const fields: FieldConfig<RelationsTableFilters>[] = [
			{
				key: isSeller ? "seller_name" : "buyer_name",
				type: "search",
				placeholder: isSeller
					? t("relation.filterSellerName")
					: t("relation.filterBuyerName"),
			},
			{
				key: "status",
				type: "multi-select",
				placeholder: t("relation.filterStatus"),
				options: statusOptions,
			},
		];

		return {
			fields,
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [isSeller, t, statusOptions]);

	const handleClearFilters = () => {
		clearFilters(toTableFilters());
	};

	return (
		<GenericFilters
			config={config}
			filters={filters}
			onFiltersChange={onFiltersChange}
			onClearFilters={handleClearFilters}
		/>
	);
};
